import * as http from 'utils/http';
import { delay } from 'redux-saga';
import { put, call, takeEvery, takeLatest, select } from 'redux-saga/effects';
import {
  setStaff,
  addStaff,
  setSingleStaff,
  updateStaff,
  setSearching,
  setFetching,
  setFetchingMore,
  setFetchingSingle,
  setPushing,
  throwError,
  resetError,
} from './duck';
import { getStaff, getStaffNextURL, getStaffById } from './selectors';
import config from 'config';
import { mapRoute } from 'reducers/routes';

// @effect Actions
const FETCH_STAFF = '@@staff/FETCH_STAFF';
const FETCH_SINGLE_STAFF = '@@staff/FETCH_SINGLE_STAFF';
const RESET_SINGLE_STAFF = '@@staff/RESET_SINGLE_STAFF';
const FETCH_MORE_STAFF = '@@staff/FETCH_MORE_STAFF';
const SEARCH_STAFF = '@@staff/SEARCH_STAFF';
const ADD_STAFF = '@@staff/ADD_STAFF';
const EDIT_STAFF = '@@staff/EDIT_STAFF';
const CLEAR_STAFF_CACHE = '@@staff/CLEAR_CACHE_STAFF';

// @effect Creators
export const clearCacheStaff = (staffUserId) => ({
  type: CLEAR_STAFF_CACHE,
  staffUserId,
});

export const fetchStaff = () => ({
  type: FETCH_STAFF,
});

export const fetchMoreStaff = () => ({
  type: FETCH_MORE_STAFF,
});

export const fetchSingleStaff = (payload) => ({
  type: FETCH_SINGLE_STAFF,
  payload,
});

export const resetSingleStaff = () => ({
  type: RESET_SINGLE_STAFF,
});

export const searchStaff = (payload) => ({
  type: SEARCH_STAFF,
  payload,
});

export const createStaff = (payload) => ({
  type: ADD_STAFF,
  payload,
});

export const editStaff = (payload) => ({
  type: EDIT_STAFF,
  payload,
});

// Workers
const loadStaff = ({ fetching, success, error, url }) =>
  function* (queryParams) {
    yield put(fetching(true));
    yield put(setSearching(!!queryParams && !!queryParams.search));
    try {
      const { pageSize } = yield select(getStaff);
      const getURL =
        url ||
        composeURL(`${config.API_URL}staff_users/?page_size=${pageSize}`, { ...queryParams });
      const { results, ...rest } = yield call(http.get, getURL);
      yield put(
        success({
          ...rest,
          results: results.map(mapStaff),
        })
      );
    } catch (e) {
      yield put(error(e));
    }
    yield put(fetching(false));
  };

function* handleFetch() {
  yield call(
    loadStaff({
      fetching: setFetching,
      success: setStaff,
      error: throwError,
    })
  );
}

function* handleFetchMore() {
  const url = yield select(getStaffNextURL);
  yield call(
    loadStaff({
      url,
      fetching: setFetchingMore,
      success: addStaff,
      error: throwError,
    })
  );
}

function* handleFetchSingle({ payload }) {
  const staff = yield select(getStaffById, payload);
  staff
    ? yield put(setSingleStaff(staff))
    : yield call(
        loadStaff({
          url: `${config.API_URL}staff_users/${payload}/`,
          fetching: setFetchingSingle,
          success: setSingleStaff,
          error: throwError,
        })
      );
}

function* handleClearCache({ staffUserId }) {
  try {
    yield call(http._delete, `${config.API_URL}users/${staffUserId}/access_tokens/`);
  } catch (e) {
    yield put(throwError(e));
  }
}

function* handleSearch({ payload }) {
  yield call(delay, 500);
  yield call(
    loadStaff({
      fetching: setFetching,
      success: setStaff,
      error: throwError,
    }),
    {
      search: payload,
    }
  );
}

function* handleCreation({ payload }) {
  yield put(setPushing(true));
  yield put(resetError());
  try {
    yield call(http.post, `${config.API_URL}staff_users/`, payload);
  } catch (e) {
    const code = e.code;
    const body = yield e.response.json();
    yield put(throwError(code, body));
  }

  // refresh users list
  yield call(
    loadStaff({
      fetching: setFetching,
      success: setStaff,
      error: throwError,
    })
  );

  yield put(setPushing(false));
}

function* handleEdition({ payload }) {
  yield put(setPushing(true));
  yield put(resetError());
  try {
    const staff = yield call(http.patch, `${config.API_URL}staff_users/${payload.id}/`, payload);
    yield put(updateStaff(mapStaff(staff)));
  } catch (e) {
    const code = e.code;
    const body = yield e.response.json();
    yield put(throwError(code, body));
  }
  yield put(setPushing(false));
}

function* handleResetSingleStaff() {
  yield put(setSingleStaff());
}

// Watchers
export function* watchStaff() {
  yield takeLatest(CLEAR_STAFF_CACHE, handleClearCache);
  yield takeLatest(SEARCH_STAFF, handleSearch);
  yield takeLatest(FETCH_STAFF, handleFetch);
  yield takeEvery(FETCH_MORE_STAFF, handleFetchMore);
  yield takeEvery(FETCH_SINGLE_STAFF, handleFetchSingle);
  yield takeEvery(RESET_SINGLE_STAFF, handleResetSingleStaff);
  yield takeLatest(ADD_STAFF, handleCreation);
  yield takeLatest(EDIT_STAFF, handleEdition);
}

// Util functions
const composeURL = (ep, params) =>
  Object.keys(params).reduce((query, key) => `${query}&${key}=${params[key]}`, `${ep}`);

const mapStaff = ({ routes, ...staff }) => ({
  ...staff,
  routes: routes.map(mapRoute(true)),
});
